/**
 * API Client Configuration with httpOnly Cookie Authentication
 * Coláiste Lurgan Management System - Secure API integration
 */

import axios from 'axios';

// API Base Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with httpOnly cookie support
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // Essential for httpOnly cookies
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// CSRF Token Management
let csrfTokenPromise = null; // Cache for ongoing CSRF token requests
let csrfTokenValue = null;  // In-memory token (cross-domain cookies aren't readable by JS)

/**
 * Get CSRF token from cookie (set by Django backend)
 */
export const getCSRFTokenFromCookie = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

/**
 * Initialize CSRF token - call this on app startup to preload the token
 */
export const initializeCSRFToken = async () => {
  try {
    const response = await apiClient.get('/api/auth/csrf-token/');
    // Store token from response body (cross-domain cookies aren't readable by JS)
    if (response.data?.csrfToken) {
      csrfTokenValue = response.data.csrfToken;
    }
    return true;
  } catch (error) {
    // CSRF token initialization failed - will retry on first request
    return false;
  }
};

/**
 * Ensure CSRF token is available for requests
 * Prevents race conditions by caching the promise for ongoing requests
 */
const ensureCSRFToken = async () => {
  // Try in-memory token first (works cross-domain), then fall back to cookie (same-domain dev)
  if (csrfTokenValue) {
    return csrfTokenValue;
  }
  const cookieToken = getCSRFTokenFromCookie();
  if (cookieToken) {
    return cookieToken;
  }

  // If there's already a request in progress, wait for it
  if (csrfTokenPromise) {
    await csrfTokenPromise;
    return getCSRFTokenFromCookie();
  }

  // Start a new CSRF token request and cache the promise
  csrfTokenPromise = initializeCSRFToken();

  try {
    await csrfTokenPromise;
    csrfTokenPromise = null; // Clear the promise cache
    return getCSRFTokenFromCookie();
  } catch (error) {
    csrfTokenPromise = null; // Clear the promise cache on error
    // Failed to ensure CSRF token - network or server error
    return null;
  }
};

// Request Interceptor - Add CSRF token and auth token to requests
apiClient.interceptors.request.use(
  async (config) => {
    // Add Authorization token if available
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      config.headers['Authorization'] = `Token ${authToken}`;
    }

    // Add CSRF token to state-changing requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      const token = await ensureCSRFToken();
      if (token) {
        config.headers['X-CSRFToken'] = token;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - Handle authentication errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const { response } = error;
    
    // Handle CSRF token errors
    if (response?.status === 403 && response?.data?.detail?.includes('CSRF')) {
      // CSRF token expired, refreshing...
      try {
        // Clear cached token and promise to force refresh
        csrfTokenValue = null;
        csrfTokenPromise = null;

        // Get fresh CSRF token
        const newToken = await ensureCSRFToken();
        if (newToken) {
          // Retry the original request with new token
          const originalRequest = error.config;
          originalRequest.headers['X-CSRFToken'] = newToken;
          return apiClient(originalRequest);
        }
      } catch (csrfError) {
        // Failed to refresh CSRF token
      }
    }

    // Handle authentication errors
    if (response?.status === 401) {
      // Clear any stored auth state
      csrfTokenPromise = null;
      // Redirect to login or emit auth error event
      window.dispatchEvent(new CustomEvent('auth-error', {
        detail: { message: 'Authentication required' }
      }));
    }
    
    return Promise.reject(error);
  }
);

// API Helper Functions
export const api = {
  // Authentication
  auth: {
    login: async (credentials) => {
      const response = await apiClient.post('/api/auth/login/', credentials);
      return response.data;
    },
    
    logout: async () => {
      const response = await apiClient.post('/api/auth/logout/');
      // Clear promise cache after logout
      csrfTokenPromise = null;
      return response.data;
    },
    
    getProfile: async () => {
      const response = await apiClient.get('/api/auth/profile/');
      return response.data;
    },
    
    register: async (userData) => {
      const response = await apiClient.post('/api/auth/register/', userData);
      return response.data;
    },

    verifyMfa: async ({ mfa_session, otp }) => {
      const response = await apiClient.post('/api/auth/verify-mfa/', { mfa_session, otp });
      return response.data;
    },

    patchProfile: async (data) => {
      const response = await apiClient.patch('/api/auth/profile/', data);
      return response.data;
    },

    changeEmail: async (new_email) => {
      const response = await apiClient.post('/api/auth/change-email/', { new_email });
      return response.data;
    },

    changePassword: async (userId, data) => {
      const response = await apiClient.post(`/api/auth/users/${userId}/change-password/`, data);
      return response.data;
    },

    myChildren: async () => {
      const response = await apiClient.get('/api/auth/my-children/');
      return response.data;
    },

    forgotPassword: async (email) => {
      const response = await apiClient.post('/api/auth/password-reset/', { email });
      return response.data;
    },

    resetPassword: async (token, new_password, new_password_confirm) => {
      const response = await apiClient.post('/api/auth/password-reset/confirm/', { token, new_password, new_password_confirm });
      return response.data;
    },

    impersonate: async (userId) => {
      const response = await apiClient.post(`/api/auth/users/${userId}/impersonate/`);
      return response.data;
    },

    stopImpersonating: async () => {
      const response = await apiClient.post('/api/auth/stop-impersonating/');
      return response.data;
    },
  },

  // Students
  students: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/students/', { params });
      return response.data;
    },
    
    getById: async (id) => {
      const response = await apiClient.get(`/api/students/students/${id}/`);
      return response.data;
    },
    
    create: async (studentData) => {
      const response = await apiClient.post('/api/students/students/', studentData);
      return response.data;
    },
    
    update: async (id, studentData) => {
      const response = await apiClient.patch(`/api/students/students/${id}/`, studentData);
      return response.data;
    },
    
    delete: async (id) => {
      const response = await apiClient.delete(`/api/students/students/${id}/`);
      return response.data;
    },

    // Bulk operations
    bulkCreate: async (studentsData) => {
      const response = await apiClient.post('/api/students/students/bulk_create/', studentsData);
      return response.data;
    },

    // Student analytics
    getAnalytics: async () => {
      const response = await apiClient.get('/api/students/students/statistics/');
      return response.data;
    },

    // Get all student UUIDs matching filters (lightweight for bulk selection)
    getUuids: async (params = {}) => {
      const response = await apiClient.get('/api/students/students/uuids/', { params });
      return response.data;
    },

    // Score student interview (ADMIN, OFFICE, INTERVIEWER only)
    scoreInterview: async (studentUuid, scoreData) => {
      const response = await apiClient.post(
        `/api/students/students/${studentUuid}/score_interview/`,
        scoreData
      );
      return response.data;
    },

    // Other enrolments sharing this student's confirmed identity link (manage_students only)
    getLinkedEnrolments: async (studentUuid) => {
      const response = await apiClient.get(`/api/students/students/${studentUuid}/linked-enrolments/`);
      return response.data;
    },

    // Resolve a single upload issue by index (ADMIN/OFFICE only)
    resolveIssue: async (studentUuid, index) => {
      const response = await apiClient.post(
        `/api/students/students/${studentUuid}/resolve_issue/`,
        { index }
      );
      return response.data;
    },

    // Flag student as ACTION_REQUIRED (ADMIN/OFFICE only)
    flag: async (studentUuid, data) => {
      const response = await apiClient.post(
        `/api/students/students/${studentUuid}/flag/`,
        data
      );
      return response.data;
    },

    // Trigger Oide sync for a single student (ADMIN/OFFICE only)
    syncOide: async (studentUuid) => {
      const response = await apiClient.post(
        `/api/students/students/${studentUuid}/sync-oide/`
      );
      return response.data;
    },

    // Bulk queue Oide sync for all unsynced students with email (ADMIN only)
    syncOideBulk: async () => {
      const response = await apiClient.post('/api/students/students/sync-oide-bulk/');
      return response.data;
    },

    // Get Oide sync summary counts (ADMIN/OFFICE only)
    oideSyncStats: async () => {
      const response = await apiClient.get('/api/students/students/oide-sync-stats/');
      return response.data;
    },

    // Get Claru deposit webhook insight (ADMIN/OFFICE only)
    claruStats: async () => {
      const response = await apiClient.get('/api/students/students/claru-stats/');
      return response.data;
    },
  },

  // Courses
  courses: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/courses/', { params });
      return response.data;
    },
    
    getById: async (id) => {
      const response = await apiClient.get(`/api/students/courses/${id}/`);
      return response.data;
    },
    
    create: async (courseData) => {
      const response = await apiClient.post('/api/students/courses/', courseData);
      return response.data;
    },
    
    update: async (id, courseData) => {
      const response = await apiClient.put(`/api/students/courses/${id}/`, courseData);
      return response.data;
    },

    delete: async (id) => {
      const response = await apiClient.delete(`/api/students/courses/${id}/`);
      return response.data;
    },

    // Course-specific endpoints
    getEnrollmentDetails: async (id) => {
      const response = await apiClient.get(`/api/students/courses/${id}/enrollment_details/`);
      return response.data;
    },

    duplicate: async (id, duplicateData) => {
      const response = await apiClient.post(`/api/students/courses/${id}/duplicate/`, duplicateData);
      return response.data;
    },

    archive: async (id, { confirmation_phrase, password }) => {
      const response = await apiClient.post(`/api/students/courses/${id}/archive/`, { confirmation_phrase, password });
      return response.data;
    },

    unarchive: async (id, { confirmation_phrase, password }) => {
      const response = await apiClient.post(`/api/students/courses/${id}/unarchive/`, { confirmation_phrase, password });
      return response.data;
    },

    dismissArchiveNudge: async (id) => {
      const response = await apiClient.post(`/api/students/courses/${id}/dismiss-archive-nudge/`);
      return response.data;
    },

    getAnalytics: async () => {
      const response = await apiClient.get('/api/students/courses/analytics/');
      return response.data;
    },
  },

  // Travel Locations
  travelLocations: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/travel-locations/', { params });
      return response.data;
    },

    getById: async (id) => {
      const response = await apiClient.get(`/api/students/travel-locations/${id}/`);
      return response.data;
    },

    create: async (travelLocationData) => {
      const response = await apiClient.post('/api/students/travel-locations/', travelLocationData);
      return response.data;
    },

    update: async (id, travelLocationData) => {
      const response = await apiClient.put(`/api/students/travel-locations/${id}/`, travelLocationData);
      return response.data;
    },

    delete: async (id) => {
      const response = await apiClient.delete(`/api/students/travel-locations/${id}/`);
      return response.data;
    },

    // Travel location-specific endpoints
    getStudentAssignments: async (id) => {
      const response = await apiClient.get(`/api/students/travel-locations/${id}/student_assignments/`);
      return response.data;
    },
  },

  // Houses
  houses: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/houses/', { params });
      return response.data;
    },

    getByUuid: async (uuid) => {
      const response = await apiClient.get(`/api/students/houses/${uuid}/`);
      return response.data;
    },

    create: async (houseData) => {
      const response = await apiClient.post('/api/students/houses/', houseData);
      return response.data;
    },

    update: async (uuid, houseData) => {
      const response = await apiClient.patch(`/api/students/houses/${uuid}/`, houseData);
      return response.data;
    },

    delete: async (uuid) => {
      const response = await apiClient.delete(`/api/students/houses/${uuid}/`);
      return response.data;
    },

    getOccupancy: async (uuid) => {
      const response = await apiClient.get(`/api/students/houses/${uuid}/occupancy_details/`);
      return response.data;
    },

    assignStudents: async (uuid, studentUuids) => {
      const response = await apiClient.post(`/api/students/houses/${uuid}/assign_students/`, {
        student_uuids: studentUuids,
      });
      return response.data;
    },

    removeStudents: async (uuid, studentUuids) => {
      const response = await apiClient.delete(`/api/students/houses/${uuid}/remove_students/`, {
        data: { student_uuids: studentUuids },
      });
      return response.data;
    },
  },

  // House Allocation
  allocation: {
    getSummary: async (includePast = false) => {
      const response = await apiClient.get('/api/students/allocation/summary/', {
        params: includePast ? { include_past: 'true' } : {},
      });
      return response.data;
    },

    getData: async (courseUuid) => {
      const response = await apiClient.get('/api/students/allocation/', {
        params: { course: courseUuid },
      });
      return response.data;
    },

    move: async (studentUuids, houseUuid, cuntoirUuids = []) => {
      const response = await apiClient.post('/api/students/allocation/move/', {
        student_uuids: studentUuids,
        cuntoir_uuids: cuntoirUuids,
        house_uuid: houseUuid ?? null,
      });
      return response.data;
    },

    clear: async (courseUuid, password, gender) => {
      const response = await apiClient.post('/api/students/allocation/clear/', {
        course_uuid: courseUuid,
        password,
        gender,
      });
      return response.data;
    },
  },

  // Class Allocation
  classAllocation: {
    getSummary: async (includePast = false) => {
      const response = await apiClient.get('/api/students/class-allocation/summary/', {
        params: includePast ? { include_past: 'true' } : {},
      });
      return response.data;
    },

    getMyClasses: async () => {
      const response = await apiClient.get('/api/students/class-allocation/my_classes/');
      return response.data;
    },

    getData: async (courseUuid) => {
      const response = await apiClient.get('/api/students/class-allocation/', {
        params: { course: courseUuid },
      });
      return response.data;
    },

    move: async (studentUuids, classGroupUuid) => {
      const response = await apiClient.post('/api/students/class-allocation/move/', {
        student_uuids: studentUuids,
        class_group_uuid: classGroupUuid ?? null,
      });
      return response.data;
    },

    autoAssign: async (courseUuid, reassignAll = false) => {
      const response = await apiClient.post('/api/students/class-allocation/auto_assign/', {
        course_uuid: courseUuid,
        reassign_all: reassignAll,
      });
      return response.data;
    },

    clear: async (courseUuid, password) => {
      const response = await apiClient.post('/api/students/class-allocation/clear/', {
        course_uuid: courseUuid,
        password,
      });
      return response.data;
    },

    getTeachers: async () => {
      const response = await apiClient.get('/api/students/classes/teacher_assignments/');
      return response.data.teachers ?? [];
    },

    renameClass: async (classUuid, name) => {
      const response = await apiClient.patch(`/api/students/classes/${classUuid}/`, { name });
      return response.data;
    },

    assignTeacher: async (classUuid, teacherId) => {
      const response = await apiClient.patch(`/api/students/classes/${classUuid}/`, {
        teacher: teacherId,
      });
      return response.data;
    },
  },

  // Cúntóirí
  cuntoirs: {
    list: async (courseUuid) => {
      const response = await apiClient.get('/api/students/cuntoirs/', {
        params: courseUuid ? { course: courseUuid } : {},
      });
      return response.data;
    },

    create: async (data) => {
      const response = await apiClient.post('/api/students/cuntoirs/', data);
      return response.data;
    },

    update: async (uuid, data) => {
      const response = await apiClient.patch(`/api/students/cuntoirs/${uuid}/`, data);
      return response.data;
    },

    delete: async (uuid) => {
      await apiClient.delete(`/api/students/cuntoirs/${uuid}/`);
    },

    upload: async (file, fieldMapping) => {
      const formData = new FormData();
      formData.append('file', file);
      if (fieldMapping) formData.append('field_mapping', JSON.stringify(fieldMapping));
      const response = await apiClient.post('/api/students/cuntoirs/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  },

  // House Course Capacities
  houseCapacities: {
    capacityMatrix: async (includePast = false) => {
      const response = await apiClient.get(
        '/api/students/house-course-capacities/capacity_matrix/',
        { params: includePast ? { include_past: 'true' } : {} }
      );
      return response.data;
    },

    bulkUpdate: async (capacities) => {
      const response = await apiClient.post(
        '/api/students/house-course-capacities/bulk_update/',
        { capacities }
      );
      return response.data;
    },
  },

  // Classes
  classes: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/classes/', { params });
      return response.data;
    },
    
    getById: async (id) => {
      const response = await apiClient.get(`/api/students/classes/${id}/`);
      return response.data;
    },
    
    create: async (classData) => {
      const response = await apiClient.post('/api/students/classes/', classData);
      return response.data;
    },
    
    update: async (id, classData) => {
      const response = await apiClient.put(`/api/students/classes/${id}/`, classData);
      return response.data;
    },
  },

  // School Years
  schoolYears: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/school-years/', { params });
      return response.data;
    },

    getById: async (id) => {
      const response = await apiClient.get(`/api/students/school-years/${id}/`);
      return response.data;
    },

    create: async (schoolYearData) => {
      const response = await apiClient.post('/api/students/school-years/', schoolYearData);
      return response.data;
    },

    update: async (id, schoolYearData) => {
      const response = await apiClient.put(`/api/students/school-years/${id}/`, schoolYearData);
      return response.data;
    },

    delete: async (id) => {
      const response = await apiClient.delete(`/api/students/school-years/${id}/`);
      return response.data;
    },
  },


  payments: {
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/payments/', { params });
      return response.data;
    },

    getByStudent: async (studentUuid) => {
      const response = await apiClient.get('/api/students/payments/', {
        params: { student: studentUuid }
      });
      return response.data;
    },

    getById: async (uuid) => {
      const response = await apiClient.get(`/api/students/payments/${uuid}/`);
      return response.data;
    },

    create: async (paymentData) => {
      const response = await apiClient.post('/api/students/payments/', paymentData);
      return response.data;
    },

    update: async (uuid, paymentData) => {
      const response = await apiClient.patch(`/api/students/payments/${uuid}/`, paymentData);
      return response.data;
    },

    delete: async (uuid) => {
      const response = await apiClient.delete(`/api/students/payments/${uuid}/`);
      return response.data;
    },

    uploadPreview: async (formData) => {
      const response = await apiClient.post('/api/students/payments/upload_preview/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    uploadCommit: async (data) => {
      const response = await apiClient.post('/api/students/payments/upload_commit/', data);
      return response.data;
    },

    resolve: async (uuid, data) => {
      const response = await apiClient.patch(`/api/students/payments/${uuid}/resolve/`, data);
      return response.data;
    },

    financialSummary: async () => {
      const response = await apiClient.get('/api/students/payments/financial_summary/');
      return response.data;
    },

    studentPaymentSummaries: async () => {
      const response = await apiClient.get('/api/students/payments/student_payment_summaries/');
      return response.data;
    },
  },

  // Email Communications
  emails: {
    // Get all emails (including drafts and sent)
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/emails/', { params });
      return response.data;
    },

    // Get specific email by ID
    getById: async (id) => {
      const response = await apiClient.get(`/api/students/emails/${id}/`);
      return response.data;
    },

    // Get all drafts
    getDrafts: async () => {
      const response = await apiClient.get('/api/students/emails/drafts/');
      return response.data;
    },

    // Create new draft
    createDraft: async (draftData) => {
      const response = await apiClient.post('/api/students/emails/create_draft/', draftData);
      return response.data;
    },

    // Update existing draft
    updateDraft: async (id, draftData) => {
      const response = await apiClient.patch(`/api/students/emails/${id}/update_draft/`, draftData);
      return response.data;
    },

    // Delete draft
    deleteDraft: async (id) => {
      const response = await apiClient.delete(`/api/students/emails/${id}/delete_draft/`);
      return response.data;
    },

    // Archive/unarchive a campaign
    archiveCampaign: async (id) => {
      const response = await apiClient.post(`/api/students/emails/${id}/archive/`);
      return response.data;
    },

    // Send draft to recipients
    sendDraft: async (id, recipientData) => {
      const response = await apiClient.post(`/api/students/emails/${id}/send_draft/`, recipientData);
      return response.data;
    },

    // Send campaign to multiple recipients
    sendCampaign: async (campaignData) => {
      const response = await apiClient.post('/api/students/emails/send_campaign/', campaignData);
      return response.data;
    },

    // Send test email for preview
    sendTestEmail: async (testEmailData) => {
      const response = await apiClient.post('/api/students/emails/send_test_email/', testEmailData);
      return response.data;
    },

    // Get email analytics
    getAnalytics: async (params = {}) => {
      const response = await apiClient.get('/api/students/emails/analytics/', { params });
      return response.data;
    },

    // Get student email history
    getStudentHistory: async (studentId) => {
      const response = await apiClient.get('/api/students/emails/student_history/', {
        params: { student_id: studentId }
      });
      return response.data;
    },

    // Get campaign summaries (group by campaign_name with success/failure counts)
    getCampaignSummary: async (params = {}) => {
      const response = await apiClient.get('/api/students/emails/campaign_summary/', { params });
      return response.data;
    },

    // Get full recipient lists for a specific campaign (on-demand, not included in summary)
    getCampaignRecipients: async (campaignUuid) => {
      const response = await apiClient.get('/api/students/emails/campaign_recipients/', { params: { campaign_uuid: campaignUuid } });
      return response.data;
    },

    // Mark individual email as acknowledged
    markAsAcknowledged: async (emailUuid) => {
      const response = await apiClient.post(`/api/students/emails/${emailUuid}/mark_as_acknowledged/`);
      return response.data;
    },

    // Mark multiple emails as acknowledged (bulk action)
    bulkAcknowledge: async (emailUuids) => {
      const response = await apiClient.post('/api/students/emails/bulk_acknowledge/', {
        email_uuids: emailUuids
      });
      return response.data;
    },

    // Check Celery task status (for campaign progress tracking)
    getTaskStatus: async (taskId) => {
      const response = await apiClient.get('/api/students/emails/task_status/', {
        params: { task_id: taskId }
      });
      return response.data;
    },

    // ========== Email Attachments ==========

    // Upload attachment to draft email
    uploadAttachment: async (emailUuid, formData) => {
      const response = await apiClient.post(
        `/api/students/emails/${emailUuid}/upload-attachment/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },

    // List all attachments for an email
    listAttachments: async (emailUuid) => {
      const response = await apiClient.get(`/api/students/emails/${emailUuid}/attachments/`);
      return response.data;
    },

    // Delete attachment from draft email
    deleteAttachment: async (emailUuid, attachmentUuid) => {
      const response = await apiClient.delete(
        `/api/students/emails/${emailUuid}/delete-attachment/${attachmentUuid}/`
      );
      return response.data;
    },

    // Download attachment
    downloadAttachment: async (emailUuid, attachmentUuid) => {
      const response = await apiClient.get(
        `/api/students/emails/${emailUuid}/download-attachment/${attachmentUuid}/`,
        {
          responseType: 'blob', // Important for file downloads
        }
      );
      return response;
    },
  },

  // System Settings API
  systemSettings: {
    // Get current system settings
    get: async () => {
      const response = await apiClient.get('/api/students/settings/system/');
      return response.data;
    },

    // Update system settings (partial update)
    update: async (settings) => {
      const response = await apiClient.patch('/api/students/settings/system/', settings);
      return response.data;
    },
  },

  // Field Permissions API (Admin only, except getMyFields)
  permissions: {
    // Get the permission matrix (all fields × all roles)
    getMatrix: async () => {
      const response = await apiClient.get('/api/students/permissions/field-permissions/matrix/');
      return response.data;
    },

    // Bulk update permissions
    bulkUpdate: async (changes) => {
      const response = await apiClient.post('/api/students/permissions/field-permissions/bulk_update/', { changes });
      return response.data;
    },

    // Get configurable fields metadata
    getConfigurableFields: async () => {
      const response = await apiClient.get('/api/students/permissions/field-permissions/configurable_fields/');
      return response.data;
    },

    // Get audit log
    getAuditLog: async (params = {}) => {
      const response = await apiClient.get('/api/students/permissions/audit-log/', { params });
      return response.data;
    },

    // Get current user's field permissions (any authenticated user)
    getMyFields: async () => {
      const response = await apiClient.get('/api/students/permissions/field-permissions/my_fields/');
      return response.data;
    },
  },

  // Page Permissions API (Admin only, except my_pages)
  pagePermissions: {
    // Get the permission matrix (all pages × all roles)
    getMatrix: async () => {
      const response = await apiClient.get('/api/students/permissions/page-permissions/matrix/');
      return response.data;
    },

    // Bulk update permissions
    bulkUpdate: async (changes) => {
      const response = await apiClient.post('/api/students/permissions/page-permissions/bulk_update/', { changes });
      return response.data;
    },

    // Get configurable pages metadata
    getConfigurablePages: async () => {
      const response = await apiClient.get('/api/students/permissions/page-permissions/configurable_pages/');
      return response.data;
    },

    // Get current user's accessible pages (all authenticated users)
    getMyPages: async () => {
      const response = await apiClient.get('/api/students/permissions/page-permissions/my_pages/');
      return response.data;
    },

    // Get audit log
    getAuditLog: async (params = {}) => {
      const response = await apiClient.get('/api/students/permissions/page-audit-log/', { params });
      return response.data;
    },
  },

  // Email Templates API (ADMIN + OFFICE)
  emailTemplates: {
    getAll: async () => {
      const response = await apiClient.get('/api/auth/email-templates/');
      return response.data;
    },
    get: async (name) => {
      const response = await apiClient.get(`/api/auth/email-templates/${name}/`);
      return response.data;
    },
    // data: { subject?, body_text? }
    update: async (name, data) => {
      const response = await apiClient.patch(`/api/auth/email-templates/${name}/`, data);
      return response.data;
    },
  },

  // Student Friendships API
  friendships: {
    // Get all friendships (optionally filtered by student)
    getAll: async (params = {}) => {
      const response = await apiClient.get('/api/students/student-friends/', { params });
      return response.data;
    },

    // Get friendships for a specific student (using backend OR filter - DIRECT friendships only)
    getByStudent: async (studentUuid) => {
      const response = await apiClient.get('/api/students/student-friends/', {
        params: { student: studentUuid }  // Backend filters: student1=UUID OR student2=UUID
      });
      return response.data;
    },

    // Get complete friendship group (including transitive friendships)
    getStudentGroup: async (studentUuid) => {
      const response = await apiClient.get('/api/students/student-friends/student_group/', {
        params: { student: studentUuid }  // Returns ALL friendships in the student's group
      });
      return response.data;
    },

    // Create a new friendship
    create: async (friendshipData) => {
      const response = await apiClient.post('/api/students/student-friends/', friendshipData);
      return response.data;
    },

    // Delete a friendship
    delete: async (friendshipUuid) => {
      const response = await apiClient.delete(`/api/students/student-friends/${friendshipUuid}/`);
      return response.data;
    },

    // Get friend groups
    getFriendGroups: async () => {
      const response = await apiClient.get('/api/students/student-friends/friend_groups/');
      return response.data;
    },
  },

  // Audit Log
  auditLog: {
    getAll: async (params = {}) => {
      const { action, ...rest } = params;
      const sp = new URLSearchParams();
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') sp.append(k, v);
      });
      if (action && action.length) action.forEach(a => sp.append('action', a));
      const response = await apiClient.get(`/api/students/audit-log/?${sp.toString()}`);
      return response.data;
    },
    markReviewed: async (uuid) => {
      const response = await apiClient.post(`/api/students/audit-log/${uuid}/mark_reviewed/`);
      return response.data;
    },
  },

  identityReviews: {
    getAll: async () => {
      const response = await apiClient.get('/api/students/identity-reviews/');
      return response.data;
    },
    confirm: async (uuid, candidateUuid) => {
      const response = await apiClient.post(`/api/students/identity-reviews/${uuid}/confirm/`, {
        candidate_uuid: candidateUuid,
      });
      return response.data;
    },
    reject: async (uuid) => {
      const response = await apiClient.post(`/api/students/identity-reviews/${uuid}/reject/`);
      return response.data;
    },
  },
};

export default api;